/**
 * Pure ordering and tree primitives for SourceContent container trees, shared
 * by the admin source tree builder and the coaching curriculum Sources tab.
 *
 * The (order, createdAt, id) comparator mirrors getSourceContent's children
 * ordering; listSourceContent still orders (order, title, id) server-side and
 * is re-sorted client-side with this comparator (server convergence tracked in
 * LG-297). The container-before-item full-tie rule is defined here and only
 * here.
 */

export interface OrderedRef {
  id: string
  order: number
  createdAt: string
}

/** A node that participates in the parentId hierarchy. */
export interface TreeNodeRef extends OrderedRef {
  parentId: string | null
}

export function compareOrderedRefs(a: OrderedRef, b: OrderedRef): number {
  if (a.order !== b.order) return a.order - b.order
  // ISO-8601 UTC strings compare correctly as strings.
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

export type MixedChild<C extends OrderedRef, I extends OrderedRef> =
  | { type: 'container'; value: C }
  | { type: 'item'; value: I }

export function mergeChildren<C extends OrderedRef, I extends OrderedRef>(
  containers: C[],
  items: I[],
): MixedChild<C, I>[] {
  const entries: MixedChild<C, I>[] = [
    ...containers.map((value) => ({ type: 'container' as const, value })),
    ...items.map((value) => ({ type: 'item' as const, value })),
  ]
  return entries.sort((a, b) => {
    const cmp = compareOrderedRefs(a.value, b.value)
    if (cmp !== 0) return cmp
    if (a.type === b.type) return 0
    return a.type === 'container' ? -1 : 1
  })
}

/** Group live containers by parentId (null = source root), buckets sorted. */
export function buildContainerTree<C extends TreeNodeRef>(
  contents: C[],
): Map<string | null, C[]> {
  const byParent = new Map<string | null, C[]>()
  for (const c of contents) {
    const key = c.parentId ?? null
    const bucket = byParent.get(key)
    if (bucket) bucket.push(c)
    else byParent.set(key, [c])
  }
  for (const bucket of byParent.values()) bucket.sort(compareOrderedRefs)
  return byParent
}

/**
 * Transitive descendant ids of rootId. Visited-set guarded so a corrupt
 * parentId cycle in data cannot hang the UI.
 */
export function descendantIdsOf<C extends TreeNodeRef>(
  contents: C[],
  rootId: string,
): Set<string> {
  const childIdsByParent = new Map<string, string[]>()
  for (const c of contents) {
    if (!c.parentId) continue
    const bucket = childIdsByParent.get(c.parentId)
    if (bucket) bucket.push(c.id)
    else childIdsByParent.set(c.parentId, [c.id])
  }
  const result = new Set<string>()
  const queue = [rootId]
  while (queue.length > 0) {
    const current = queue.pop()
    if (current === undefined) break
    for (const childId of childIdsByParent.get(current) ?? []) {
      if (!result.has(childId)) {
        result.add(childId)
        queue.push(childId)
      }
    }
  }
  return result
}

export interface EstimatedRef {
  id: string
  estimatedMinutes: number | null
}

export interface SourceRollup {
  /** Leaf memberships in the whole subtree. */
  itemCount: number
  /** Sum of the estimates that are known. */
  estimatedMinutes: number
  /** True when at least one contributor had no estimate, so the sum is partial. */
  hasUnknownEstimate: boolean
}

/**
 * Roll a container's contents up into counts and time.
 *
 * itemCount always walks the full subtree. The estimate is authoritative
 * wherever it is authored: a child container with its own estimatedMinutes
 * contributes that value rather than its computed subtotal, and its unknowns
 * do not propagate. Estimates are frequently null (problem items always
 * project null), so a partial sum is reported via hasUnknownEstimate rather
 * than silently coerced to zero.
 *
 * Visited-set guarded: a corrupt parentId cycle in data must not hang the UI.
 */
export function rollupSourceNode(
  containerId: string,
  containersByParent: ReadonlyMap<
    string | null,
    readonly (TreeNodeRef & EstimatedRef)[]
  >,
  itemsByContainer: ReadonlyMap<string, readonly EstimatedRef[]>,
): SourceRollup {
  const visited = new Set<string>()

  const walk = (id: string): SourceRollup => {
    if (visited.has(id)) {
      return { itemCount: 0, estimatedMinutes: 0, hasUnknownEstimate: false }
    }
    visited.add(id)

    let itemCount = 0
    let estimatedMinutes = 0
    let hasUnknownEstimate = false

    for (const item of itemsByContainer.get(id) ?? []) {
      itemCount += 1
      if (item.estimatedMinutes === null) hasUnknownEstimate = true
      else estimatedMinutes += item.estimatedMinutes
    }

    for (const child of containersByParent.get(id) ?? []) {
      const childRollup = walk(child.id)
      itemCount += childRollup.itemCount
      if (child.estimatedMinutes === null) {
        estimatedMinutes += childRollup.estimatedMinutes
        if (childRollup.hasUnknownEstimate) hasUnknownEstimate = true
      } else {
        estimatedMinutes += child.estimatedMinutes
      }
    }

    return { itemCount, estimatedMinutes, hasUnknownEstimate }
  }

  return walk(containerId)
}

/**
 * Roll up every container in one bottom-up pass, so a renderer can look a
 * container's rollup up by id instead of re-walking its subtree on every
 * render (otherwise every expand/collapse re-walks the whole tree, and
 * nested rows redundantly re-walk subtrees their ancestors already walked).
 *
 * Aggregation matches rollupSourceNode exactly. It lives beside that helper
 * rather than at the call site because the two are one algorithm: when this
 * caching layer was written separately it silently lost the cycle guard
 * below, which is the entire reason that guard exists.
 *
 * Cycle safety needs BOTH sets here. `rollups` memoizes finished subtrees,
 * but an entry is only written after recursion returns, so on a cyclic
 * parentId chain (a → b → a) the re-entrant call would still miss the cache
 * and recurse forever. `inProgress` catches that re-entry and contributes
 * zero, exactly as rollupSourceNode's visited set does.
 */
export function buildSourceRollups(
  containersByParent: ReadonlyMap<
    string | null,
    readonly (TreeNodeRef & EstimatedRef)[]
  >,
  itemsByContainer: ReadonlyMap<string, readonly EstimatedRef[]>,
): Map<string, SourceRollup> {
  const rollups = new Map<string, SourceRollup>()
  const inProgress = new Set<string>()

  const visit = (id: string): SourceRollup => {
    const cached = rollups.get(id)
    if (cached) return cached
    if (inProgress.has(id)) {
      return { itemCount: 0, estimatedMinutes: 0, hasUnknownEstimate: false }
    }
    inProgress.add(id)

    let itemCount = 0
    let estimatedMinutes = 0
    let hasUnknownEstimate = false

    for (const item of itemsByContainer.get(id) ?? []) {
      itemCount += 1
      if (item.estimatedMinutes === null) hasUnknownEstimate = true
      else estimatedMinutes += item.estimatedMinutes
    }

    for (const child of containersByParent.get(id) ?? []) {
      const childRollup = visit(child.id)
      itemCount += childRollup.itemCount
      if (child.estimatedMinutes === null) {
        estimatedMinutes += childRollup.estimatedMinutes
        if (childRollup.hasUnknownEstimate) hasUnknownEstimate = true
      } else {
        estimatedMinutes += child.estimatedMinutes
      }
    }

    const rollup: SourceRollup = {
      itemCount,
      estimatedMinutes,
      hasUnknownEstimate,
    }
    inProgress.delete(id)
    rollups.set(id, rollup)
    return rollup
  }

  for (const containers of containersByParent.values()) {
    for (const container of containers) visit(container.id)
  }

  return rollups
}
